import React, { useState, useEffect } from 'react';
import {
  Share2,
  Sparkles,
  X,
  Check,
  AlertCircle,
  Target,
  BarChart3,
  Flame,
  Swords,
} from 'lucide-react';
import { formatAchievementName } from '../utils/achievementFormat.ts';
import { formatDisplayNumber } from '../config/scoringConfig.ts';

interface ShareToFeedModalProps {
  isOpen: boolean;
  onClose: () => void;
  userUid: string;
  achievementCode?: string;
  achievementTitle?: string;
  achievementDescription?: string;
  username?: string;
  userAvatar?: string | null;
  defaultContent?: string;
  badgeCode?: string;
  postType?: 'scripture_milestone' | 'challenge_mode' | string;
  score?: number;
  accuracy?: number;
  bestStreak?: number;
  xpGained?: number;
  userRank?: string;
  churchName?: string;
  onPublished?: (post: any) => void;
  onPostCreated?: () => void;
}

export const ShareToFeedModal: React.FC<ShareToFeedModalProps> = ({
  isOpen,
  onClose,
  userUid,
  achievementCode = 'MILESTONE',
  achievementTitle = 'Scripture Milestone',
  achievementDescription,
  username,
  userAvatar,
  defaultContent = '',
  badgeCode,
  postType = 'milestone',
  score = 0,
  accuracy = 0,
  bestStreak = 0,
  xpGained = 0,
  userRank = '',
  churchName = '',
  onPublished,
  onPostCreated,
}) => {
  const [reflection, setReflection] = useState(defaultContent);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    setReflection(defaultContent);
  }, [defaultContent, isOpen]);

  const effectiveCode = badgeCode || achievementCode;
  const isChallenge =
    postType === 'challenge_mode' ||
    effectiveCode === 'CHALLENGE_MODE' ||
    (achievementTitle && achievementTitle.toLowerCase().includes('challenge'));

  const effectiveTitle = isChallenge
    ? 'Challenge Mode'
    : achievementTitle && achievementTitle !== 'Scripture Milestone'
    ? achievementTitle
    : formatAchievementName(effectiveCode) || 'Scripture Milestone';

  if (!isOpen) return null;

  const charLimit = 200;
  const remaining = charLimit - reflection.length;

  const containsLinks = /(https?:\/\/|www\.|\.com|\.org|\.net|\.io|\.gg)/i.test(
    reflection
  );

  const handleShare = async () => {
    if (containsLinks) {
      setErrorMsg('External links are not allowed in feed posts.');
      return;
    }
    if (reflection.length > charLimit) {
      setErrorMsg(`Post exceeds limit of ${charLimit} characters.`);
      return;
    }

    setIsSubmitting(true);
    setErrorMsg(null);

    const defaultMsg = isChallenge
      ? `Completed a Challenge Mode session with ${formatDisplayNumber(score).text} points (${accuracy}% accuracy)! All glory to God! 📖🙏`
      : `Praising God for reaching this Bible study milestone: ${effectiveTitle}!`;

    try {
      const res = await fetch('/api/feed/posts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userUid,
          username,
          userAvatar,
          postType: isChallenge ? 'challenge_mode' : 'scripture_milestone',
          achievementCode: effectiveCode,
          achievementTitle: effectiveTitle,
          churchName,
          content: reflection.trim() || defaultMsg,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        if (onPublished) onPublished(data.post || data);
        if (onPostCreated) onPostCreated();
        onClose();
      } else {
        const err = await res.json().catch(() => ({}));
        setErrorMsg(err.error || 'Failed to publish to community feed.');
      }
    } catch {
      setErrorMsg('Network error publishing to feed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="share-feed-title"
      data-testid="share-to-feed-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-xs"
    >
      <div className="w-full max-w-md rounded-3xl bg-[#141414] border border-[#262626] p-6 shadow-2xl space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[#262626] pb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-2xl bg-[#1C1C1C] text-[#F97316] flex items-center justify-center shrink-0">
              <Share2 className="w-5 h-5" />
            </div>
            <div>
              <h2
                id="share-feed-title"
                className="text-base font-bold text-[#FAFAFA]"
              >
                Share to Community Feed?
              </h2>
              <p className="text-xs text-[#A3A3A3]">
                Encourage fellow scholars with your achievement
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-[#A3A3A3] hover:text-[#FAFAFA] hover:bg-[#1C1C1C] transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Milestone / Challenge Preview Card */}
        {isChallenge ? (
          <div className="p-4 rounded-2xl bg-[#18181A] border border-[#FF8A00]/40 space-y-3 shadow-sm">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-[#FF8A00]/15 border border-[#FF8A00]/30 text-[#FF8A00] text-xs font-bold uppercase tracking-wider">
                <Swords className="w-3.5 h-3.5" />
                Challenge Mode
              </span>
            </div>

            {/* 4-Stat Grid Preview */}
            <div className="grid grid-cols-4 gap-2 pt-1 border-t border-[#262626] text-center">
              <div>
                <div className="text-[10px] text-[#A3A3A3] flex items-center justify-center gap-1">
                  <Target className="w-3 h-3 text-[#A3A3A3]" /> Score
                </div>
                <div className={`text-xs font-bold font-mono mt-0.5 ${score <= 0 ? 'text-[#737373]' : 'text-white'}`}>
                  {formatDisplayNumber(score).text}
                </div>
              </div>

              <div>
                <div className="text-[10px] text-[#A3A3A3] flex items-center justify-center gap-1">
                  <BarChart3 className="w-3 h-3 text-[#10B981]" /> Acc
                </div>
                <div className="text-xs font-bold text-[#10B981] font-mono mt-0.5">
                  {accuracy}%
                </div>
              </div>

              <div>
                <div className="text-[10px] text-[#A3A3A3] flex items-center justify-center gap-1">
                  <Flame className="w-3 h-3 text-[#F59E0B]" /> Streak
                </div>
                <div className={`text-xs font-bold font-mono mt-0.5 ${bestStreak <= 0 ? 'text-[#737373]' : 'text-[#F59E0B]'}`}>
                  {formatDisplayNumber(bestStreak).text}
                </div>
              </div>

              <div>
                <div className="text-[10px] text-[#A3A3A3] flex items-center justify-center gap-1">
                  XP Gained
                </div>
                <div className={`text-xs font-bold font-mono mt-0.5 ${xpGained <= 0 ? 'text-[#737373]' : 'text-white'}`}>
                  {xpGained <= 0 ? '–' : `+${formatDisplayNumber(xpGained).text}`}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-3.5 rounded-2xl bg-gradient-to-r from-amber-500/10 to-[#1C1C1C] border border-[#525252] flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 text-amber-400 flex items-center justify-center shrink-0 font-bold text-lg">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-mono uppercase tracking-wider text-amber-400 font-bold">
                Scripture Milestone
              </div>
              <div className="text-sm font-bold text-[#FAFAFA] truncate">
                {effectiveTitle}
              </div>
              {achievementDescription && (
                <div className="text-xs text-[#A3A3A3] truncate">
                  {achievementDescription}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Note / Reflection (text-only up to 200 chars) */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label
              htmlFor="feed-reflection"
              className="block text-xs font-semibold text-[#D4D4D4]"
            >
              Add a brief reflection or praise (optional)
            </label>
            <span
              className={`text-[11px] font-mono ${
                remaining < 0
                  ? 'text-red-500 font-bold'
                  : 'text-[#A3A3A3]'
              }`}
            >
              {reflection.length}/{charLimit}
            </span>
          </div>
          <textarea
            id="feed-reflection"
            rows={3}
            maxLength={charLimit}
            placeholder="Share what God taught you, or a word of encouragement..."
            value={reflection}
            onChange={(e) => {
              setReflection(e.target.value);
              if (errorMsg) setErrorMsg(null);
            }}
            className="w-full rounded-2xl border border-[#262626] bg-[#1C1C1C] p-3 text-xs text-[#FAFAFA] focus:outline-none focus:ring-2 focus:ring-[#525252] resize-none"
          />
          <p className="text-[11px] text-[#A3A3A3] mt-1">
            Text-only up to 200 characters. Photos, links, and documents are disallowed per Community Guidelines.
          </p>
        </div>

        {errorMsg && (
          <div
            role="alert"
            className="p-2.5 rounded-xl bg-red-500/10 border border-red-500/20 text-xs text-red-400 flex items-center gap-1.5"
          >
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{errorMsg}</span>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-[#262626]">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl border border-[#262626] text-xs font-semibold text-[#D4D4D4] hover:bg-[#1C1C1C] transition-colors"
          >
            Not Now
          </button>
          <button
            type="button"
            disabled={isSubmitting || remaining < 0 || containsLinks}
            onClick={handleShare}
            data-testid="confirm-share-to-feed"
            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-xl bg-gradient-to-r from-[#FF8A00] to-[#EA580C] hover:brightness-110 disabled:opacity-50 text-white text-xs font-semibold transition-all shadow-sm cursor-pointer"
          >
            <Check className="w-4 h-4" />
            <span>{isSubmitting ? 'Sharing...' : 'Share to Feed'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};
